/**
 * Queue Status page — live view of the Shopify sync queues.
 *
 * Auto-refreshes every few seconds and accumulates a client-side history so
 * partners can see the *trajectory* of each queue and — via drain
 * projections — when their changes will be live on Shopify.
 *
 * Every aggregate on this page is computed over the queues that reported
 * successfully, so each one states its coverage: a metrics outage must never
 * render as a calm, zeroed board.
 */
import { useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';

import { POLL_INTERVAL_MS } from './constants';
import QueueDepthChart from './QueueDepthChart';
import Sparkline from './Sparkline';
import {
  formatAgo,
  formatClockTime,
  formatCount,
  formatEstimate,
  formatThroughput,
  HEALTH_CHIP_STYLES,
  HEALTH_LABELS,
  queueColor,
  queueDisplayName,
  queueHealth,
  type QueueHealth,
} from './queueDisplay';
import { drainProgress, pendingHistoryFor } from './queueSeries';
import { useQueueHistory } from './useQueueHistory';
import type { QueueMetrics, QueueSample } from './types';

const HEALTH_ICONS: Record<QueueHealth, React.ReactElement> = {
  clear: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
  draining: <SyncIcon sx={{ fontSize: 14 }} />,
  waiting: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
  unavailable: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
};

const HealthChip = ({ health }: { health: QueueHealth }) => {
  const style = HEALTH_CHIP_STYLES[health];
  return (
    <Chip
      size="small"
      icon={HEALTH_ICONS[health]}
      label={HEALTH_LABELS[health]}
      sx={{
        bgcolor: style.bg,
        color: style.fg,
        fontWeight: 600,
        '& .MuiChip-icon': { color: style.fg },
      }}
    />
  );
};

/** Message from a failed poll, when the data layer gave us one worth showing. */
const errorDetail = (error: unknown): string | null => {
  const status =
    typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number' ?
      error.status :
      null;
  const message = error instanceof Error && error.message ? error.message : null;
  if (status == null && message == null) return null;
  return [status == null ? null : `HTTP ${status}`, message].filter(Boolean).join(' · ');
};

/** Re-renders every second so "updated Xs ago" ticks between polls. */
const UpdatedAgo = ({ lastUpdatedAt }: { lastUpdatedAt: number | null }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1_000);
    return () => clearInterval(interval);
  }, []);
  if (lastUpdatedAt == null) return null;
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
      Updated
      {' '}
      {formatAgo((Date.now() - lastUpdatedAt) / 1000)}
    </Typography>
  );
};

const LiveDot = ({ live }: { live: boolean }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: live ? 'success.main' : 'grey.500',
        display: 'inline-block',
        ...(live && {
          animation: 'queuePulse 2s ease-out infinite',
          '@keyframes queuePulse': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.45)}` },
            '70%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.success.main, 0)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}` },
          },
        }),
      }}
    />
  );
};

interface StatTileProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  spark?: number[];
  sparkColor?: string;
}

const StatTile = ({ label, value, sub, accent, spark, sparkColor }: StatTileProps) => {
  const theme = useTheme();
  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1}>
        <Typography
          component="div"
          sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: accent ? 'primary.main' : 'text.primary' }}
        >
          {value}
        </Typography>
        {spark && spark.length > 1 && (
          <Box sx={{ pb: 0.25, flexShrink: 0 }}>
            <Sparkline values={spark} color={sparkColor ?? theme.palette.primary.main} width={72} />
          </Box>
        )}
      </Stack>
      {sub && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {sub}
        </Typography>
      )}
    </Card>
  );
};

/**
 * Drain-progress meter: how much of the session's peak backlog has cleared.
 * "Session peak" is the peak within the retained history window, so it can
 * fall as old samples age out — this is a progress cue, not an audit trail.
 */
const DrainMeter = ({ progress, color, label }: { progress: number, color: string, label: string }) => (
  <Box
    sx={{ height: 6, borderRadius: 3, bgcolor: alpha(color, 0.15), overflow: 'hidden' }}
    role="progressbar"
    aria-label={label}
    aria-valuenow={Math.round(progress * 100)}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <Box
      sx={{
        width: `${Math.round(progress * 100)}%`,
        height: '100%',
        borderRadius: 3,
        bgcolor: color,
        transition: 'width 0.6s ease',
      }}
    />
  </Box>
);

interface QueueCardProps {
  queue: QueueMetrics;
  history: QueueSample[];
  lastUpdatedAt: number | null;
}

const QueueCard = ({ queue, history, lastUpdatedAt }: QueueCardProps) => {
  const color = queueColor(queue.name);
  const health = queueHealth(queue);
  const pendingHistory = pendingHistoryFor(history, queue.name);
  const progress = drainProgress(queue.messagesPending, pendingHistory);
  const clearAt =
    lastUpdatedAt != null &&
    queue.messagesPending > 0 &&
    queue.estimatedMinutesToClear != null &&
    queue.estimatedMinutesToClear > 0 ?
      lastUpdatedAt + queue.estimatedMinutesToClear * 60_000 :
      null;

  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
          {queueDisplayName(queue.name)}
        </Typography>
        <HealthChip health={health} />
      </Stack>

      {queue.error != null ? (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {queue.error}
        </Alert>
      ) : (
        <>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Typography component="div" sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>
                {formatCount(queue.messagesPending)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                pending
              </Typography>
            </Box>
            <Sparkline values={pendingHistory.slice(-48)} color={color} />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <strong>{formatCount(queue.messagesProcessing)}</strong>
              {' '}
              processing
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <strong>{formatThroughput(queue.throughputPerMinute)}</strong>
              {' '}
              throughput
            </Typography>
          </Stack>

          <DrainMeter
            progress={progress}
            color={color}
            label={`${queueDisplayName(queue.name)} drain progress`}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {queue.messagesPending <= 0 ?
                'Backlog cleared' :
                `${Math.round(progress * 100)}% of session peak cleared`}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {queue.messagesPending <= 0 ?
                '—' :
                clearAt != null ?
                  `clear ~${formatClockTime(clearAt)}` :
                  'no estimate'}
            </Typography>
          </Stack>
        </>
      )}
    </Card>
  );
};

const QueueStatusList = () => {
  const { queues, history, lastUpdatedAt, live, setLive, refetch, error, isPending } =
    useQueueHistory();

  const healthy = useMemo(() => queues.filter(queue => queue.error == null), [queues]);
  const unavailableCount = queues.length - healthy.length;
  const allUnavailable = queues.length > 0 && healthy.length === 0;

  const totals = useMemo(() => {
    const pending = healthy.reduce((sum, queue) => sum + queue.messagesPending, 0);
    const processing = healthy.reduce((sum, queue) => sum + queue.messagesProcessing, 0);
    const throughputs = healthy
      .map(queue => queue.throughputPerMinute)
      .filter((v): v is number => v != null);
    const throughput = throughputs.length ?
      throughputs.reduce((sum, v) => sum + v, 0) :
      null;

    const backlogged = healthy.filter(queue => queue.messagesPending > 0);
    let allClearAt: number | null | 'unknown' = null;
    if (backlogged.length > 0) {
      const etas = backlogged
        .map(queue => queue.estimatedMinutesToClear)
        .filter((eta): eta is number => eta != null && eta > 0);
      allClearAt = etas.length === backlogged.length ?
        (lastUpdatedAt ?? Date.now()) + Math.max(...etas) * 60_000 :
        'unknown';
    }
    return { pending, processing, throughput, allClearAt };
  }, [healthy, lastUpdatedAt]);

  // Only sum samples in which every queue being totalled reported — otherwise
  // a queue dropping out mid-window draws as a cliff that looks like progress.
  const totalPendingHistory = useMemo(() => {
    const names = healthy.map(queue => queue.name);
    if (names.length === 0) return [];
    return history.flatMap((sample) => {
      const values = names.map(name => sample.pending[name]);
      return values.some(v => v == null) ? [] : [values.reduce<number>((sum, v) => sum + (v ?? 0), 0)];
    });
  }, [healthy, history]);

  const coverageSub =
    unavailableCount > 0 ? `across ${healthy.length} of ${queues.length} queues` : 'across all queues';
  const partialNote = unavailableCount > 0 ? ` · ${unavailableCount} unavailable` : '';

  const minutesToAllClear =
    typeof totals.allClearAt === 'number' ? (totals.allClearAt - Date.now()) / 60_000 : null;
  const allClearValue =
    totals.allClearAt == null ?
      'All caught up' :
      totals.allClearAt === 'unknown' ?
        '—' :
        `~${formatClockTime(totals.allClearAt)}`;
  const allClearSub =
    totals.allClearAt == null ?
      'No messages waiting' :
      totals.allClearAt === 'unknown' ?
        'No throughput data to estimate' :
        minutesToAllClear != null && minutesToAllClear <= 0 ?
          'Estimate elapsed — still working' :
          `in ${formatEstimate(minutesToAllClear)}`;

  const detail = errorDetail(error);

  return (
    <Box sx={{ pb: 3 }}>
      <Title title="Queue Status" />

      {/* header — title and description come from StorefrontTabShell */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="flex-end"
        spacing={1}
        sx={{ mt: 1, mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LiveDot live={live} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: live ? 'success.main' : 'text.secondary' }}>
              {live ? `Live · every ${POLL_INTERVAL_MS / 1000}s` : 'Paused'}
            </Typography>
          </Stack>
          <UpdatedAgo lastUpdatedAt={lastUpdatedAt} />
          <Tooltip title={live ? 'Pause auto-refresh' : 'Resume auto-refresh'}>
            <IconButton size="small" onClick={() => setLive(!live)}>
              {live ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isPending && <LinearProgress sx={{ mb: 2 }} />}

      {Boolean(error) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Could not load queue status. It will retry automatically while auto-refresh is on.
          {detail != null && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
              {detail}
            </Box>
          )}
        </Alert>
      )}

      {!isPending && !error && queues.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No sync queues were reported. This is unexpected — contact support rather than reading
          this as an empty backlog.
        </Alert>
      )}

      {allUnavailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Queue metrics are unavailable for every queue right now, so there are no current figures
          to show. Messages may still be queued — see the per-queue errors below.
        </Alert>
      )}

      {!allUnavailable && queues.length > 0 && (
        <>
          {/* KPI tiles */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 2,
            }}
          >
            <StatTile
              label="Messages pending"
              value={formatCount(totals.pending)}
              sub={coverageSub}
              spark={totalPendingHistory.slice(-48)}
            />
            <StatTile
              label="Processing now"
              value={formatCount(totals.processing)}
              sub={`being sent to Shopify${partialNote}`}
            />
            <StatTile
              label="Sync throughput"
              value={totals.throughput == null ? '—' : `${totals.throughput.toFixed(0)}/min`}
              sub={`15-min average, delayed ~15 min${partialNote}`}
            />
            <StatTile
              label="Est. all clear"
              value={allClearValue}
              sub={allClearSub}
              accent={totals.allClearAt != null && totals.allClearAt !== 'unknown'}
            />
          </Box>

          {/* depth chart */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1">Queue depth</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              Messages pending per queue since this page opened, with each queue&apos;s projected
              drain to zero at its recent throughput.
            </Typography>
            <QueueDepthChart history={history} queues={queues} />
          </Card>
        </>
      )}

      {/* per-queue cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(Math.max(queues.length, 1), 3)}, 1fr)` },
          gap: 2,
          mb: 2,
        }}
      >
        {queues.map(queue => (
          <QueueCard key={queue.name} queue={queue} history={history} lastUpdatedAt={lastUpdatedAt} />
        ))}
      </Box>

      {/* details table — current numbers without needing the chart's tooltip */}
      <Card sx={{ overflow: 'auto' }}>
        <Table size="small" aria-label="Queue details">
          <TableHead>
            <TableRow>
              <TableCell>Queue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Processing</TableCell>
              <TableCell align="right">Throughput</TableCell>
              <TableCell align="right">Est. time to clear</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queues.map(queue => (
              <TableRow key={queue.name}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: queueColor(queue.name) }} />
                    <span>{queueDisplayName(queue.name)}</span>
                  </Stack>
                </TableCell>
                <TableCell>
                  <HealthChip health={queueHealth(queue)} />
                </TableCell>
                {queue.error != null ? (
                  <TableCell colSpan={4} sx={{ color: 'error.main' }}>
                    {queue.error}
                  </TableCell>
                ) : (
                  <>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(queue.messagesPending)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(queue.messagesProcessing)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatThroughput(queue.throughputPerMinute)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatEstimate(queue.estimatedMinutesToClear)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5, px: 0.5 }}>
        Pending and processing counts are current. Throughput is a 15-minute average measured
        roughly 15 minutes ago — AWS publishes it on a delay — and the clear-time estimates
        extrapolate from it, assuming no new messages are added. Status reflects the live counts
        rather than that delayed rate: “Draining” means messages are in flight right now,
        “Waiting” means a backlog with nothing currently being sent.
      </Typography>
    </Box>
  );
};

export default QueueStatusList;
